import { fetchAuthSession } from 'aws-amplify/auth';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import BlueDotsBackground from '../BlueDotsBackground';
import SubscribeButton from '../auth/SubscribeButton';
import {
  BORDER,
  COLOR_ARRIVED_END,
  COLOR_ARRIVED_START,
  COLOR_NOT_TRACKED,
  COLOR_PENDING,
  DISCLAIMER,
  DISCLAIMER_SUBSET,
  TITLE_SUFFIX,
  center,
  computeMap,
  formatRefreshed,
  hexPoints,
} from '../shared/mapModel';

const DATA_URL = '/mcd/data.json';

function Hex({
  x,
  y,
  arrived,
  tracked = true,
  code,
  scale = 1,
  fontSize = 16,
  label,
  interactive = true,
  isHovered = false,
  onEnter,
  onLeave,
}) {
  const { cx, cy } = center(x, y);
  const fill =
    !tracked ? COLOR_NOT_TRACKED
    : arrived ? 'url(#arrivedGradient)'
    : COLOR_PENDING;
  return (
    <g
      onMouseEnter={interactive && onEnter ? () => onEnter(code) : undefined}
      onMouseLeave={interactive && onLeave ? () => onLeave() : undefined}
      style={
        interactive ?
          {
            cursor: 'pointer',
            transition: 'filter 160ms ease',
            filter:
              isHovered ?
                'brightness(1.05) drop-shadow(0 0 3px rgba(120,160,240,0.35))'
              : 'none',
          }
        : undefined
      }
    >
      <polygon
        points={hexPoints(cx, cy, scale)}
        fill={fill}
        stroke={BORDER}
        strokeWidth={1.5}
      />
      {label ?
        <text x={cx} y={cy} textAnchor="middle" fill="#ffffff">
          <tspan x={cx} dy="-0.1em" fontSize={fontSize} fontWeight="bold">
            {label.top}
          </tspan>
          <tspan x={cx} dy="1.3em" fontSize={fontSize * 0.5} fontWeight="600">
            {label.bottom}
          </tspan>
        </text>
      : <text
          x={cx}
          y={cy}
          textAnchor="middle"
          dominantBaseline="central"
          fill="#ffffff"
          fontSize={fontSize}
          fontWeight="bold"
        >
          {code}
        </text>
      }
    </g>
  );
}

function LegendItem({ color, label }) {
  return (
    <span className="flex items-center gap-2">
      <span
        className="inline-block h-4 w-4 rounded-sm"
        style={{ backgroundColor: color }}
      />
      {label}
    </span>
  );
}

export default function McdMap() {
  const [tiles, setTiles] = useState(null);
  const [percent, setPercent] = useState(0);
  const [quarter, setQuarter] = useState('');
  const [lastRefreshed, setLastRefreshed] = useState('');
  const [error, setError] = useState(null);
  const [hovered, setHovered] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const session = await fetchAuthSession();
        const token = session?.tokens?.idToken?.toString();
        const res = await fetch(DATA_URL, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (cancelled) return;
        const {
          tiles: built,
          percent: pct,
          quarter: qtr,
          lastRefreshed: refreshed,
        } = computeMap(data);
        setTiles(built);
        setPercent(pct);
        setQuarter(qtr);
        setLastRefreshed(refreshed);
      } catch (err) {
        if (!cancelled) setError(err.message);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const title = quarter ? `${quarter} ${TITLE_SUFFIX}` : TITLE_SUFFIX;

  // Render the hovered tile last so it paints above its neighbours.
  const orderedTiles =
    tiles && hovered ?
      [
        ...tiles.filter((t) => t.code !== hovered),
        ...tiles.filter((t) => t.code === hovered),
      ]
    : tiles;

  return (
    <>
      <BlueDotsBackground />
      <div className="relative min-h-screen text-white px-4 py-10 flex flex-col items-center justify-center">
        <div className="w-full max-w-7xl mx-auto">
          <div className="relative mb-8">
            <div className="flex items-center justify-center gap-3">
              <h1 className="text-3xl md:text-4xl font-bold text-center">
                {title}
              </h1>
              <span className="inline-block bg-yellow-500/20 border border-yellow-500 text-yellow-300 px-3 py-1 rounded-full text-sm font-semibold">
                BETA
              </span>
            </div>
            <div className="mt-4 flex justify-center md:mt-0 md:absolute md:right-0 md:top-1/2 md:-translate-y-1/2">
              <SubscribeButton />
            </div>
          </div>

          {error && (
            <p className="text-center text-red-400">
              Unable to load the progress map. Please try again later.
            </p>
          )}

          {tiles && (
            <svg
              viewBox="-110 10 960 468"
              className="w-full mx-auto"
              role="img"
              aria-label={title}
            >
              <defs>
                <linearGradient
                  id="arrivedGradient"
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop offset="0" stopColor={COLOR_ARRIVED_START} />
                  <stop offset="1" stopColor={COLOR_ARRIVED_END} />
                </linearGradient>
              </defs>
              {orderedTiles.map((t) => (
                <Hex
                  key={t.code}
                  x={t.x}
                  y={t.y}
                  arrived={t.arrived}
                  tracked={t.tracked}
                  code={t.code}
                  isHovered={hovered === t.code}
                  onEnter={setHovered}
                  onLeave={() => setHovered(null)}
                />
              ))}
              <Hex
                x={4}
                y={13}
                arrived
                scale={2}
                fontSize={30}
                label={{ top: `${percent}%`, bottom: 'Overall' }}
                interactive={false}
              />
              {lastRefreshed && (
                <text
                  x={-110}
                  y={472}
                  textAnchor="start"
                  fill="#9ca3af"
                  fontSize={15}
                >
                  {`Last refreshed: ${formatRefreshed(lastRefreshed)}`}
                </text>
              )}
            </svg>
          )}

          {tiles && (
            <div className="mt-2 flex items-center justify-center gap-6 text-sm font-semibold text-white">
              <LegendItem color={COLOR_ARRIVED_START} label="Available" />
              <LegendItem color={COLOR_PENDING} label="Not Available" />
              <LegendItem color={COLOR_NOT_TRACKED} label="Not Tracked" />
            </div>
          )}

          <p className="text-center text-gray-500 text-sm italic mt-6">
            {DISCLAIMER}
            <br />
            {DISCLAIMER_SUBSET}
          </p>
          <div className="mt-6 flex justify-center">
            <Link
              to="/contact"
              className="inline-block bg-blue-500 hover:bg-blue-400 text-white font-semibold text-lg px-8 py-4 rounded-lg shadow-lg transition-colors"
            >
              Track Your Organization's Invoices
            </Link>
          </div>
          <p className="text-center text-gray-400 text-sm mt-3 max-w-3xl mx-auto">
            Tracking can be done specific to your organization's invoices
            allowing for additional visibility into availability by invoice,
            upcoming due invoices, prioritization, processing completion,
            forecasted payment amounts, dispute trends, and anomaly detection.
            If you would like to increase your visibility, reach out above at
            &ldquo;Track Your Organization&apos;s Invoices&rdquo;.
          </p>
        </div>
      </div>
    </>
  );
}
